import type {
  RecommendationBatch,
  RecommendationSession,
  RecommendationTransport,
} from '../domain/recommendation'
import { StoryFailure, type Visit } from '../domain/story'
import type {
  PendingStorage,
  StoryClock,
  StoryContext,
  StoryTransport,
} from './ports'
import { PendingDelivery } from './pending-delivery'
import { retryDelay } from './request-policy'

type Options = {
  transport: RecommendationTransport
  receipts: StoryTransport
  storage: PendingStorage
  clock: StoryClock
  uuid: () => string
  platform: 'web' | 'pwa'
  navigate: (path: string) => void
  canNavigate: (path: string) => boolean
  onManualError?: () => void
}
export type RecommendationSnapshot = {
  batch: RecommendationBatch | null
  visible: boolean
  busy: boolean
  ready: boolean
}
const inactive: StoryContext = {
  account: null,
  visible: false,
  inCabinet: false,
  allowed: false,
  blocked: false,
}
export class RecommendationRuntime {
  private manualRequested = false
  private presenceReady = false
  private context = inactive
  private session: RecommendationSession | null = null
  private batch: RecommendationBatch | null = null
  private batchClient: string | null = null
  private rendered = false
  private confirmed = false
  private busy = false
  private epoch = 0
  private cancelDay?: () => void
  private cancelReserve?: () => void
  private cancelRetry?: () => void
  private listeners = new Set<() => void>()
  private snapshot: RecommendationSnapshot = {
    batch: null,
    visible: false,
    busy: false,
    ready: false,
  }
  private readonly pending: PendingDelivery
  constructor(private readonly options: Options) {
    this.pending = new PendingDelivery(
      options.storage,
      options.receipts,
      options.clock,
      (receipt) => {
        if (this.batch?.current?.reservationId !== receipt.reservationId) return
        this.confirmed = true
        // A late ACK for the previous slide must not cancel its pending transition.
        if (receipt.firstAction && !this.busy) this.dismiss()
      },
      (id) => {
        if (this.batch?.current?.reservationId === id) this.dismiss()
      },
    )
  }
  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }
  getSnapshot = () => this.snapshot
  isActive = () => this.batch !== null || this.busy
  update(context: StoryContext) {
    if (context.account !== this.context.account) {
      this.dismiss()
      this.pending.setAccount(context.account)
    }
    this.context = context
    this.refresh()
  }
  pausePresence() {
    this.presenceReady = false
    this.publish()
  }
  setVisit(clientId: string, visit: Visit | null) {
    this.presenceReady = visit !== null
    if (
      !visit ||
      (this.batch?.mode === 'auto' && this.session?.visit.id !== visit.id)
    )
      this.dismiss()
    this.session = visit ? { clientId, visit } : null
    this.pending.wake()
    this.refresh()
  }
  start(batch: RecommendationBatch, clientId: string) {
    if (
      !this.context.account ||
      !this.session ||
      this.session.clientId !== clientId ||
      this.isActive()
    )
      return
    this.batchClient = clientId
    this.accept(batch)
  }
  requestManual() {
    if (
      !this.context.account ||
      !(this.context.manualAllowed ?? this.context.allowed) ||
      this.isActive()
    )
      return
    this.manualRequested = true
    this.refresh()
  }
  async manual(id: string | null) {
    if (!this.ready() || !this.session || this.isActive())
      throw new StoryFailure('contract', 'STORY_NOT_READY')
    const session = this.session,
      epoch = ++this.epoch
    this.busy = true
    this.publish()
    try {
      const command = {
        clientId: session.clientId,
        visitId: session.visit.id,
        platform: this.options.platform,
        recommendationId: id,
        ...(id === null ? { loop: true } : {}),
        key: this.options.uuid(),
      }
      const batch = await this.retry(
        () => this.options.transport.manual(command),
        epoch,
      )
      if (epoch !== this.epoch) return
      this.batchClient = session.clientId
      this.busy = false
      this.accept(batch)
    } catch (error) {
      if (epoch === this.epoch) {
        this.busy = false
        this.publish()
        throw error
      }
    }
  }
  displayed(id: string) {
    if (
      this.batch?.current?.reservationId !== id ||
      this.rendered ||
      !this.snapshot.visible
    )
      return
    this.rendered = true
    this.enqueue(null)
  }
  previous() {
    if (this.batch && (this.batch.cursor > 0 || this.batch.looping))
      this.advance('tap', false, true)
  }
  advance(reason: 'tap' | 'timer', finish = false, previous = false) {
    const batch = this.batch,
      current = batch?.current,
      clientId = this.batchClient
    if (!batch || !current || !clientId || this.busy || !this.snapshot.visible)
      return
    const epoch = this.epoch
    const completing = finish && !batch.looping
    this.busy = true
    this.publish()
    const command = {
      clientId,
      batchId: batch.id,
      reservationId: current.reservationId,
      reason,
      finish: completing,
      ...(previous ? { previous: true } : {}),
      key: this.options.uuid(),
    }
    if (completing) {
      const account = this.context.account
      void this.retry(
        () => this.options.transport.next(command),
        epoch,
        0,
        () => this.context.account === account && account !== null,
      ).catch(() => {})
      this.dismiss()
      return
    }
    void this.retry(() => this.options.transport.next(command), epoch)
      .then(async (result) => {
        if (epoch !== this.epoch) return
        if (!result.closed && !result.current) {
          const activation = {
            clientId,
            batchId: batch.id,
            key: this.options.uuid(),
          }
          result = await this.retry(
            () => this.options.transport.activate(activation),
            epoch,
          )
        }
        if (epoch !== this.epoch) return
        this.busy = false
        this.accept(result)
      })
      .catch(() => {
        if (epoch === this.epoch) this.dismiss()
      })
  }
  act(action: 'cta' | 'close') {
    const current = this.batch?.current
    if (!current || !this.snapshot.visible) return
    this.rendered = true
    this.enqueue(action)
    const path = current.guideTarget.path
    this.dismiss()
    if (action === 'cta' && this.options.canNavigate(path))
      this.options.navigate(path)
  }
  unavailable() {
    this.dismiss()
  }
  destroy(clear = false) {
    this.dismiss()
    this.pending.stop()
    if (clear) this.pending.setAccount(null)
    this.session = null
    this.context = inactive
    this.publish()
  }
  private accept(batch: RecommendationBatch) {
    if (
      batch.closed ||
      !batch.current ||
      batch.current.expiresAt <= this.options.clock.now() ||
      batch.expiresAt <= this.options.clock.now() ||
      !this.options.canNavigate(batch.current.guideTarget.path)
    ) {
      this.dismiss()
      return
    }
    this.batch = batch
    this.rendered = false
    this.confirmed = false
    this.cancelDay?.()
    this.cancelReserve?.()
    this.cancelDay = this.options.clock.schedule(
      () => this.dismiss(),
      batch.expiresAt - this.options.clock.now(),
    )
    this.cancelReserve = this.options.clock.schedule(
      () => this.refresh(),
      batch.current.expiresAt - this.options.clock.now(),
    )
    this.refresh()
  }
  private refresh() {
    const batch = this.batch
    if (
      batch &&
      !this.confirmed &&
      batch.current &&
      batch.current.expiresAt <= this.options.clock.now() &&
      this.ready() &&
      !this.busy &&
      this.batchClient
    ) {
      const epoch = this.epoch,
        command = {
          clientId: this.batchClient,
          batchId: batch.id,
          key: this.options.uuid(),
        }
      this.busy = true
      void this.retry(() => this.options.transport.activate(command), epoch)
        .then((result) => {
          if (epoch !== this.epoch) return
          this.busy = false
          this.accept(result)
        })
        .catch(() => {
          if (epoch === this.epoch) this.dismiss()
        })
    }
    if (this.manualRequested && this.ready() && !this.isActive()) {
      this.manualRequested = false
      void this.manual(null).catch(() => this.options.onManualError?.())
    }
    this.publish()
  }
  private enqueue(action: 'cta' | 'close' | null) {
    const current = this.batch?.current
    if (!current || !this.context.account || !this.batchClient) return
    this.pending.enqueue({
      account: this.context.account,
      clientId: this.batchClient,
      reservationId: current.reservationId,
      action,
      deadline: current.expiresAt + 23 * 3600_000,
      impressionAccepted: this.confirmed,
    })
  }
  private ready() {
    const c = this.context
    return Boolean(
      this.presenceReady &&
      c.account &&
      this.session &&
      c.visible &&
      c.inCabinet &&
      (c.manualAllowed ?? c.allowed) &&
      !c.blocked,
    )
  }
  private publish() {
    const batch = this.batch
    const visible = Boolean(
      batch &&
      this.ready() &&
      (batch.mode === 'manual' || this.context.allowed) &&
      (this.confirmed ||
        !batch.current ||
        batch.current.expiresAt > this.options.clock.now()),
    )
    const ready = this.ready()
    if (
      this.snapshot.batch === batch &&
      this.snapshot.visible === visible &&
      this.snapshot.busy === this.busy &&
      this.snapshot.ready === ready
    )
      return
    this.snapshot = { batch, visible, busy: this.busy, ready }
    this.listeners.forEach((listener) => listener())
  }
  private dismiss() {
    this.manualRequested = false
    this.epoch++
    this.batch = null
    this.batchClient = null
    this.busy = false
    this.cancelDay?.()
    this.cancelReserve?.()
    this.cancelRetry?.()
    this.publish()
  }
  private async retry<T>(
    call: () => Promise<T>,
    epoch: number,
    attempt = 0,
    valid: () => boolean = () => epoch === this.epoch,
  ): Promise<T> {
    if (!valid()) throw new StoryFailure('contract', 'STORY_SESSION_CHANGED')
    try {
      return await call()
    } catch (error) {
      const delay = retryDelay(error, attempt)
      if (delay === null || !valid()) throw error
      await new Promise<void>((resolve) => {
        const cancel = this.options.clock.schedule(resolve, delay)
        this.cancelRetry = () => {
          cancel()
          resolve()
        }
      })
      return this.retry(call, epoch, attempt + 1, valid)
    }
  }
}
